import {
  ListItem,
  TextField,
  Autocomplete,
  ListItemText,
  createFilterOptions,
  AutocompleteChangeReason,
  AutocompleteChangeDetails
} from "@material-ui/core";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useContactTagSuggestions } from "./useContactTagSuggestions";
import type { ReactNode, SyntheticEvent } from "react";

type ContactDialogInputTagsAutocompleteProps = {
  small?: boolean;
  disabled?: boolean;
  editable?: boolean;
  selectedTags: string[];
  setSelectedTags: (tags: string[]) => void;
  /** Optional leading icon (start adornment — the right side in RTL). */
  icon?: ReactNode;
};

export function ContactDialogInputTagsAutocomplete({
  small,
  disabled,
  editable,
  selectedTags,
  setSelectedTags,
  icon
}: ContactDialogInputTagsAutocompleteProps): JSX.Element {
  const [open, setOpen] = useState(false);

  const [inputValue, setInputValue] = useState("");

  // The realtor's own tags, most used first, asked of the server each time the list opens
  // (`useContactTagSuggestions`). Nothing here waits for the answer: the field is free text,
  // and a list that lands late, or never, only means fewer suggestions.
  const tagOptions = useContactTagSuggestions(open);

  const { t: translate } = useTranslation();

  const handleTagsChange = (
    event: SyntheticEvent,
    value: AutocompleteTag[],
    _reason: AutocompleteChangeReason,
    details?: AutocompleteChangeDetails<AutocompleteTag>
  ): void => {
    const parsedTags = convertParsedTagsToRawTags(value);

    setInputValue("");
    setSelectedTags(parsedTags);
  };

  return (
    <Autocomplete
      freeSolo
      multiple
      fullWidth
      open={open}
      size={small ? "small" : undefined}
      value={convertRawTagsToParsedTags(selectedTags)}
      options={convertRawTagsToParsedTags(tagOptions)}
      disabled={disabled}
      inputValue={inputValue}
      getOptionLabel={(option) => option.value}
      isOptionEqualToValue={(option, value) => option.value === value.value}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      onChange={handleTagsChange}
      onInputChange={(_event, newInputValue, reason) => {
        if (reason !== "input") return;
        setInputValue(newInputValue);
      }}
      filterOptions={(options, params) => {
        const trimmedInputValue = params.inputValue.trim();

        const filteredOptions = filter(options, params);

        const isExistingTag = options.some(({ value }) => trimmedInputValue === value);

        const shouldAddNewTagOption =
          editable && !filteredOptions.length && trimmedInputValue && !isExistingTag;

        if (shouldAddNewTagOption) {
          filteredOptions.push({
            hint: true,
            value: trimmedInputValue
          });
        }

        return filteredOptions;
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={translate("contactsPage.tableColumns.tags")}
          InputProps={{
            ...params.InputProps,
            // Prepend the optional icon BEFORE the selected-tag chips (which
            // Autocomplete renders as the start adornment).
            startAdornment: icon ? (
              <>
                {icon}
                {params.InputProps.startAdornment}
              </>
            ) : (
              params.InputProps.startAdornment
            )
          }}
        />
      )}
      renderOption={(params, { value, hint }) => (
        <ListItem {...params} key={value}>
          {hint ? (
            <ListItemText primary={`${translate("contactsPage.control.add")} '${value}'`} />
          ) : (
            <ListItemText primary={value} />
          )}
        </ListItem>
      )}
    />
  );
}

const filter = createFilterOptions<AutocompleteTag>({
  limit: 100,
  matchFrom: "any"
});

type AutocompleteTag = {
  hint?: boolean;
  value: string;
};

type StringOrAutocompleteTag = string | AutocompleteTag;

function convertRawTagsToParsedTags(tags: string[]): AutocompleteTag[] {
  return tags.map((tag) => ({ value: tag }));
}

function convertParsedTagsToRawTags(tags: StringOrAutocompleteTag[]): string[] {
  let parsedTags: string[] = [];

  for (const tag of tags) {
    const validValue = typeof tag === "string" ? tag.trim() : tag.value.trim();

    if (!validValue) continue;

    const isDuplicate = parsedTags.some((existingTag) => existingTag === validValue);

    if (isDuplicate) continue;

    parsedTags.push(validValue);
  }

  return parsedTags;
}
